import React, {useEffect} from "react";
import AppContainer from "../../common/app_container";
import {useUserAPI} from "@src/api/user.api";
import {User} from "@src/model/user";
import {Organization} from "@src/model/organization";
import {useNavigate, useParams} from "react-router-dom";
import {SubmitHandler, useForm} from "react-hook-form";
import Alert, {AlertProps} from "@src/common/alert";
import {useOrganizationAPI} from "../../api/organization.api";
import ReactPasswordChecklist from "react-password-checklist";
import {PaperClipIcon} from '@heroicons/react/20/solid'
import BackButton from "@src/common/components/back-button";

interface UserEditProps { }
const UserEdit: React.FC<UserEditProps> = (props) => {
  const { id } = useParams();
  const userAPI = useUserAPI();
  const organizationAPI = useOrganizationAPI();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [status, setStatus] = React.useState<AlertProps | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [roles, setRoles] = React.useState<string[]>([]);
  const [organizations, setOrganizations] = React.useState<Organization[]>([]);
  const [isPasswordValid, setIsPasswordValid] = React.useState(false);
  const [origUser, setOrigUser] = React.useState<User | null>(null);

  const [stats, setStats] = React.useState<{name: string; value: string | number; unit: string}[]>([
    {name: "Num Vaults", value: 0, unit: ""},
    {name: "Num Images", value: 0, unit: ""},
    {name: "Num Videos", value: 0, unit: ""},
    {name: "Num Convos", value: 0, unit: ""},
    {name: "Last Login", value: "never", unit: ""},
    {name: "Subscription", value: "None", unit: ""},
  ]);
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<User>();
  const onSubmit: SubmitHandler<User> = (data) => {
    setIsSaving(true);
    if (typeof (data.is_active) === "string") {
      data.is_active = data.is_active === "true";
    }
    if (origUser != null && (origUser.email || "").toLowerCase().trim() !== (data.email || "").toLowerCase().trim()) {
      if (!window.confirm("It looks like you changed this user's email.  If you confirm, this will change his login name to this new email address.  Proceed?")) {
        setIsSaving(false);
        return;
      }
    }
    if (!data.user_type || (data.user_type && data.user_type.trim() === "")) {
      alert("User type is required")
      setIsSaving(false);
      return false;
    }
    var cleanData = {...data};
    if (!cleanData.industry_type || (cleanData.industry_type && cleanData.industry_type.trim() === "")) {
      delete cleanData.industry_type
    }
    if (!cleanData.organization_role || (cleanData.organization_role && cleanData.organization_role.trim() === "")) {
      delete cleanData.organization_role
    }


    userAPI
        .saveUser(cleanData)
      .then(() => {
        setIsSaving(false);
        setStatus({ type: "success", message: "User saved successfully" });
        setTimeout(() => {
          navigate("/users");
        }, 3000);
      })
      .catch((err) => {
        setIsSaving(false);
        setStatus({ type: "error", message: "Error saving user: " + err.message });
      });
  };

  useEffect(() => {
    userAPI.getRoles().then((response) => {
      setRoles(response);
    });
    organizationAPI.getAllOrganizations().then((result) => {
      setOrganizations(result.filter(org => org.organization_type === "agency"));
      if (id) {
        userAPI.getUser(id).then((response) => {
          setOrigUser(response);
          setOrganizations(result.filter(org => org.organization_type === "agency" || org.id === response.organization_id));
          reset(response);
          const sub = response.subscription;
          const subLabel = sub?.product_name
            ? `${sub.product_name}${sub.status ? ` (${sub.status})` : ""}`
            : "None";
          setStats([
            {name: "Num Vaults", value: response.num_vaults || 0, unit: ""},
            {name: "Num Images", value: response.num_images || 0, unit: ""},
            {name: "Num Videos", value: response.num_videos || 0, unit: ""},
            {name: "Num Convos", value: response.num_conversations || 0, unit: ""},
            {
              name: "Last Login",
              value: response.last_login ? new Date(response.last_login).toLocaleDateString() : "never",
              unit: ""
            },
            {name: "Subscription", value: subLabel, unit: sub?.interval ? `/ ${sub.interval}` : ""},
          ])
        });
      }
    });
  }, [id]);

  const password = watch("password")

  const fetchRandomPassword = () => {
    userAPI
      .generateRandomPassword()
      .then((response) => {
        reset({ ...watch(), password: response });
        setShowPassword(true);
      })
      .catch((err) => {
        setStatus({ type: "error", message: "Error fetching random password: " + err.message });
      });
  };

  return (
    <AppContainer>
      <div className="container">
        <BackButton variant="ghost" onClick={() => navigate("/users")} />
        <div className="flex justify-between">
          <h1 className="text-3xl">{id ? "Edit User" : "Add User"}</h1>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            type="submit"
            form="user_form"
            disabled={isSaving}
          >
            Save
          </button>
        </div>
        {status && <Alert {...status} />}
        <div className="bg-white dark:bg-gray-900">
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 gap-px bg-gray-900/5 sm:grid-cols-2 lg:grid-cols-5 dark:bg-white/10">
              {stats.map((stat) => (
                  <div key={stat.name} className="bg-white px-4 py-6 sm:px-6 lg:px-8 dark:bg-gray-900">
                    <p className="text-sm/6 font-medium text-gray-500 dark:text-gray-400">{stat.name}</p>
                    <p className="mt-2 flex items-baseline gap-x-2">
                      <span className="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
                        {stat.value}
                      </span>
                      {stat.unit ? <span className="text-sm text-gray-500 dark:text-gray-400">{stat.unit}</span> : null}
                    </p>
                  </div>
              ))}
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} id="user_form">
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <label htmlFor="first_name" className="block text-sm font-medium leading-6 text-gray-900">
                First Name
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register("first_name")}
                    type="text"
                    placeholder="First Name"
                    autoComplete="first_name"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="last_name" className="block text-sm font-medium leading-6 text-gray-900">
                First Name
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register("last_name")}
                    type="text"
                    placeholder="Last Name"
                    autoComplete="last_name"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="last_name" className="block text-sm font-medium leading-6 text-gray-900">
                Full Name
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register("full_name")}
                    type="text"
                    placeholder="Full Name"
                    autoComplete="full_name"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="is_active" className="block text-sm font-medium leading-6 text-gray-900">
                Is Active
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                    {...register("is_active")}
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option value="">-- Select Active --</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="onboarded" className="block text-sm font-medium leading-6 text-gray-900">
                Is Onboarded
              </label>
              <div className="mt-2">
                <div
                    className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                      {...register("onboarded")}
                      className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option value="">-- Select Onboarded --</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="organization_id" className="block text-sm font-medium leading-6 text-gray-900">
                Organization
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">If the user is part of a multi-user agency, create and select the organization first.</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">If the user is not part of a multi-user org, leave this field blank and an individual organization will be created for them.</p>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                    {...register("organization_id")}
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option value="">-- Select Agency Organization --</option>
                    {organizations.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name}{it.organization_type === "individual" ? "   [INDIVIDUAL ORG]" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="organization_role" className="block text-sm font-medium leading-6 text-gray-900">
                Organization Role
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                    {...register("organization_role")}
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option value="">-- Select Role --</option>
                    <option value="owner">Owner</option>
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="user_type" className="block text-sm font-medium leading-6 text-gray-900">
                User Type
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                    {...register("user_type")}
                    autoComplete="user_type"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option value="">-- Select User Type --</option>
                    <option value="creator">Creator</option>
                    <option value="rights_holder">Rights Holder</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="industry_type" className="block text-sm font-medium leading-6 text-gray-900">
                Industry Type
              </label>
              <div className="mt-2">
                <div
                    className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                      {...register("industry_type")}
                      className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option value="">-- Select Industry --</option>
                    <option value="real_estate">Real Estate</option>
                    <option value="medical_wellness">Medical/Wellness</option>
                    <option value="legal_services">Legal Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="roles" className="block text-sm font-medium leading-6 text-gray-900">
                Roles
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                    {...register("roles")}
                    multiple
                    autoComplete="roles"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                Email
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register("email")}
                    type="email"
                    placeholder="Email"
                    autoComplete="email"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                Password (leave blank if you don't want to change)
              </label>
              <div className="mt-2">
                <div className="max-w-sm justify-center items-center w-full">
                  <div className="flex flex-row">
                    <div className="relative flex-1">
                      <input {...register("password")} id="hs-toggle-password"
                        type={showPassword ? "text" : "password"}
                        className="py-2.5 sm:py-3 ps-4 pe-10 block w-full border-gray-200 rounded-lg sm:text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600"
                        placeholder="Enter password" />
                      <button type="button" onClick={() => setShowPassword((old) => !old)}
                        className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-hidden focus:text-blue-600 dark:text-neutral-600 dark:focus:text-blue-500">
                        <svg className="shrink-0 size-3.5" width="24" height="24" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path className="hs-password-active:hidden" d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                          <path className="hs-password-active:hidden"
                            d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                          <path className="hs-password-active:hidden"
                            d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                          <line className="hs-password-active:hidden" x1="2" x2="22" y1="2" y2="22"></line>
                          <path className="hidden hs-password-active:block"
                            d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                          <circle className="hidden hs-password-active:block" cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                    </div>
                    <button type="button" onClick={fetchRandomPassword} className="flex items-center ms-2 px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md">
                      <PaperClipIcon style={{ width: 20 }} /> Random
                    </button>
                  </div>
                  <ReactPasswordChecklist
                    className="text-black"
                    rules={["minLength", "specialChar", "number", "capital"]}
                    minLength={8}
                    value={password || ""}
                    valueAgain={watch("password")}
                    onChange={(isValid) => {
                      setIsPasswordValid(isValid)
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </AppContainer>
  );
};

export default UserEdit;
